// Tweaks panel — collapsible floating control for theme/accent/density/
// typography/chat-bubble/sidebar-density. Persists via the useTweaks hook
// (localStorage today, /api/me/preferences later).
//
// Simpler than the design-handoff prototype's version because we drop the
// design-tool host protocol (postMessage __edit_mode_set_keys); this is
// an end-user affordance, not a designer-facing tool.

import { useState } from "preact/hooks";
import { Icon } from "../icons";
import { ACCENT_OPTIONS, type Tweaks } from "../lib/use-tweaks";

interface TweaksPanelProps {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
}

export function TweaksPanel({ tweaks, setTweak }: TweaksPanelProps) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button className="tw-fab" title="Tweaks" onClick={() => setOpen(true)}>
        <Icon name="sparkle" size={16} />
      </button>
    );
  }

  return (
    <div className="tw-panel" data-open={open}>
      <div className="tw-head">
        <b>Tweaks</b>
        <button className="tw-x" onClick={() => setOpen(false)} title="Close">
          <Icon name="x" size={13} />
        </button>
      </div>
      <div className="tw-body">
        <div className="tw-section">Theme</div>

        <div className="tw-row">
          <span className="tw-lbl">Dark mode</span>
          <button
            className="tw-toggle"
            data-on={tweaks.dark}
            onClick={() => setTweak("dark", !tweaks.dark)}
          >
            <i />
          </button>
        </div>

        <div className="tw-row">
          <span className="tw-lbl">Accent</span>
          <div className="tw-swatches">
            {ACCENT_OPTIONS.map((c) => (
              <button
                key={c}
                className="tw-swatch"
                data-active={tweaks.accent === c}
                style={{ background: c }}
                onClick={() => setTweak("accent", c)}
                title={c}
              />
            ))}
          </div>
        </div>

        <div className="tw-row">
          <span className="tw-lbl">Density</span>
          <div className="tw-seg">
            {(["compact", "regular", "comfy"] as const).map((d) => (
              <button
                key={d}
                data-active={tweaks.density === d}
                onClick={() => setTweak("density", d)}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-row">
          <span className="tw-lbl">Typography</span>
          <select
            className="tw-select"
            value={tweaks.type}
            onChange={(e) => setTweak("type", (e.currentTarget.value as Tweaks["type"]))}
          >
            <option value="geist">Geist + Serif</option>
            <option value="editorial">Bricolage + Serif</option>
            <option value="mono">Geist + Mono</option>
          </select>
        </div>

        <div className="tw-section">Chat</div>

        <div className="tw-row">
          <span className="tw-lbl">Bubble style</span>
          <div className="tw-seg">
            {(["rounded", "square", "minimal"] as const).map((b) => (
              <button
                key={b}
                data-active={tweaks.bubble === b}
                onClick={() => setTweak("bubble", b)}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-row">
          <span className="tw-lbl">Dense sidebar</span>
          <button
            className="tw-toggle"
            data-on={tweaks.sidebarDense}
            onClick={() => setTweak("sidebarDense", !tweaks.sidebarDense)}
          >
            <i />
          </button>
        </div>
      </div>
    </div>
  );
}
